import './App.css';
import Modal from './modal';
import { isAfter, isEqual } from "date-fns";
import { nurseBaseAmount, nursePayIncrease, nurseLongevity, nurseEducation } from './data/staffNursePay.js';
import { headNurseBaseAmount, headNursePayIncrease, headNurseLongevity, headNurseEducation } from './data/headNursePay.js';
import { supervisorNurseBaseAmount, supervisorNursePayIncrease, supervisorNurseLongevity, supervisorNurseEducation } from './data/supervisorNursePay.js';
import { therapistBaseAmount, therapistPayIncrease, therapistLongevity, therapistEducation } from './data/therapistPay.js';
import { audiologistBaseAmount, audiologistPayIncrease, audiologistLongevity, audiologistEducation } from './data/audiologistPay.js';

/**
 * Renders salary schedule codes and related information based on provided props for final step in the questionnaire.
 * 
 * This component displays salary information including step codes, differentials,
 * and longevity codes. It also renders additional content and external links if provided.
 *
 * @param {Object} props - The props object containing the following properties:
 * @param {string} props.title - The title to be displayed for the salary schedule.
 * @param {string} props.stepCode - The step code used to determine the row and longevity information.
 * @param {string} props.differential - The differential code used to determine the column information.
 * @param {Array} [props.currentQuestion] - An optional array of content objects to be rendered.
 * @param {string} props.currentQuestion[].content - The HTML content to be rendered.
 * @param {string} [props.currentQuestion[].type] - The type of content, e.g., "External Link".
 * @param {string} [props.currentQuestion[].link] - The URL for external links.
 * @param {string} [props.currentQuestion[].label] - The label for external links.
 * 
 * @returns {JSX.Element} A React component displaying the formatted salary schedule information.
 */
function SalaryScheduleCodes(props) {
    let listContent;
    let convertedDifferential = '';
    let stepLetter = '';
    let stepNumber = '';
    let updatedStepLetter = '';
    let longevityCode = '+';
    let error = false;
    let listModal = [];
    const septFirst = isAfter(props.appointedDate, new Date(2019, 8, 1)) || isEqual(props.appointedDate, new Date(2019, 8, 1));
    if(props.jobTitle == "teacher" || props.jobTitle == "adult-education-teacher"){
        longevityCode = '+L';
    }
    if(props.jobTitle != "paraprofessionals"){
        stepLetter = props.stepCode.substring(1);
        stepNumber = props.stepCode.substring(0, 1);
    }

    // Format Longevity Date
    switch (stepLetter) {
        case "A":
            updatedStepLetter = 'A';
            longevityCode = '';
        break;
        case "B":
            updatedStepLetter = 'B';
            longevityCode = '';
        break;
        case "V":
            updatedStepLetter = 'A';
            longevityCode += "5";
        break;
        case "W":
            updatedStepLetter = 'B';
            longevityCode += "5";
        break;
        case "C":
            updatedStepLetter = 'A';
            longevityCode += "10";
        break;
        case "D":
            updatedStepLetter = 'B';
            longevityCode += "10";
        break;
        case "G":
            updatedStepLetter = 'A';
            longevityCode += "13";
        break;
        case "H":
            updatedStepLetter = 'B';
            longevityCode += "13";
        break;
        case "E":
            updatedStepLetter = 'A';
            longevityCode += "15";
        break;
        case "F":
            updatedStepLetter = 'B';
            longevityCode += "15";
        break;
        case "I":
            updatedStepLetter = 'A';
            longevityCode += "18";
        break;
        case "J":
            updatedStepLetter = 'B';
            longevityCode += "18";
        break;
        case "K":
            updatedStepLetter = 'A';
            longevityCode += "20";
        break;
        case "L":
            updatedStepLetter = 'B';
            longevityCode += "20";
        break;
        case "M":
            updatedStepLetter = 'A';
            longevityCode += "22";
        break;
        case "N":
            updatedStepLetter = 'B';
            longevityCode += "22";
        break;
    }

    // Make sure Lab Tech Is always 'A' after 5 years
    if(stepLetter != "B" && props.jobTitle == "lab-tech"){
        updatedStepLetter = 'A';
    }

    // Convert differentail to UFT Code
    switch (props.differential) {
        case "MA":
            convertedDifferential = "BA C1";
            break;
        case "1":
            convertedDifferential = "BA C1";
            break;
        case "PA":
            convertedDifferential = "BA+30<br/>C2";
            break;
        case "QA":
            convertedDifferential = "C2+ID";
            break;
        case "RA":
            convertedDifferential = "MA<br/>C2+PD";
            break;
        case "2":
            convertedDifferential = "MA<br/>C2+PD";
            break;
        case "SA":
            convertedDifferential = "C2+PD<br/>+ID";
            break;
        case "3":
            convertedDifferential = "C2+PD<br/>+ID";
            break;
        case "TA":
            convertedDifferential = "MA+30<br/>C6";
            break;
        case "4":
            convertedDifferential = "MA+30<br/>C6";
            break;
        case "UA":
            convertedDifferential = "MA+30<br/>C6+PD";
            break;
        case "5":
            convertedDifferential = "MA+30<br/>C6+PD";
            break;
        case "WA":
            convertedDifferential = "Base";
            break;
        case "WB":
            convertedDifferential = "Base+<br/>one diff";
            break;
        case "WE":
            convertedDifferential = "Base+<br/>two diff";
            break;
        case "WF":
            convertedDifferential = "Vlh";
            break;
        case "WH":
            convertedDifferential = "Vlh2";
            break;
        case "WT":
            convertedDifferential = "Base";
            break;
        case "WU":
            convertedDifferential = "Base + diff";
            break;
        case "WK":
            convertedDifferential = "Vlf+<br/>Vlk";
            break;
        case "WL":
            convertedDifferential = "Vlf2+<br/>Vlk2";
            break;
        case "WQ":
            convertedDifferential = "Vlf2+<br/>Vlk2";
            break;
        case "WM":
            convertedDifferential = "Vlf3+<br/>Vlk3";
            break;
        case "P1":
            convertedDifferential = "Teacher Aide";
            break;
        case "P2":
            convertedDifferential = "Ed Asst.";
            break;
        case "P3":
            convertedDifferential = "Ed. Asst. A-1";
            break;
        case "P4":
            convertedDifferential = "Ed. Asst. A-11";
            break;
        case "P5":
            convertedDifferential = "Ed. Asst. B";
            break;
        case "P6":
            convertedDifferential = "Ed. Assoc.";
            break;
        case "P9A":
            convertedDifferential = "Ed. Assoc. A/Aux. Trainer A";
            break;
        case "P9B":
            convertedDifferential = "Ed. Assoc. B/Aux. Trainer B";
            break;
        
    }

    let longevityText = '';
    let longevityYearsText = '';
    if(props.longevity){
        const longevityYears = Math.floor(props.longevity);
        if (props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
            if(1 > longevityYears){
                longevityText = "";
            }
            else if(1 === longevityYears){
                longevityText = "A/B (first year)";
            }
            else if(5 > longevityYears){
                longevityText = "A/B";
            }
            else if (6 >= longevityYears){
                longevityText = "A/B + 5 but less than 7";
            }
            else if (9 >= longevityYears){
                longevityText = "A/B + 7 but less than 10";
            }
            else if (14 >= longevityYears){
                longevityText = "A/B + 10 but less than 15";
            }
            else if (19 >= longevityYears){
                longevityText = "A/B + 15 but less than 20";
            }
            else if (21 >= longevityYears){
                longevityText = "A/B + 20 but less than 22";
            }
            else if (22 <= longevityYears){
                longevityText = "A/B + 22 years or more";
            }

            if (longevityText.startsWith('A/B +')) {
                longevityYearsText = longevityText.substring(longevityText.indexOf('+') + 1).trim();
                longevityText = 'A/B';
            }
        }
        else{
            if(5 > longevityYears){
                longevityText = "";
            }
            else if (6 >= longevityYears){
                longevityText = "5 but less than 7";
            }
            else if (9 >= longevityYears){
                longevityText = "7 but less than 10";
            }
            else if (14 >= longevityYears){
                longevityText = "10 but less than 15";
            }
            else if (19 >= longevityYears){
                longevityText = "15 but less than 20";
            }
            else if (21 >= longevityYears){
                longevityText = "20 but less than 22";
            }
            else if (22 <= longevityYears){
                longevityText = "22 years or more";
            }
        }
    }

    // Map through step status object and set corresponding step status
    let stepStatus = Object.entries(props.stepStatus).map(([key, value]) => {
        if(key == 'step-1-status'){
            if(value == 'fail'){
                error = true;
            }
        }
        if(props.jobTitle == "paraprofessionals" && key == 'step-2-status'){
            if(value == 'fail'){
                error = true;
            }
        }
    })

    let salaryScheduleTitle = props.title;
    // Update Para headline if there is an error
    if(props.jobTitle == "paraprofessionals" && error == true){
        salaryScheduleTitle = 'You may be here on the NYC DOE salary schedule:';
    }

    // Render Modal
    const modals = props.modal;
    let row = stepNumber + updatedStepLetter + longevityCode;
    if(modals && Array.isArray(modals)){
        listModal = modals.map((modalContent, index) =>
            <Modal key={`modal-${index}`} currentQuestion={modalContent.content} label={modalContent.label} title={modalContent.title} isActive={props.isModalActive} setIsActive={props.setModalIsActive} row={row} column={convertedDifferential}/>
        )
    }

    // Format degreeType for display
    const formattedDegreeType = props.degreeType 
      ? props.degreeType
          .replace(/-/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      : '';

    // Render differentials based on the current differential
    const content = props.currentQuestion;
    if(content && Array.isArray(content)){
        listContent = content.map((content, index) =>
        <>
          {(content.type == "Content" && props.hoursPerWeek != "Y" && props.jobTitle == "adult-education-teacher") && <p className='content text-link-white'>The salary reported in this row/column is for someone who works 30 hours per week. {listModal} to convert the salary for your hours per week.</p>}
          <p className='content' dangerouslySetInnerHTML={{__html: content.content}}></p>
          {content.type == "External Link" && <a href={content.link} target="_blank" rel="noopener noreferrer" className="default-button next-button external-link" dangerouslySetInnerHTML={{__html: content.label}}></a>}
        </>
      );
    }

    // Render Salary
    let totalPay = 0,
        jobBaseAmountData,
        jobPayIncreaseData,
        jobEductionData,
        jobLongevityData,
        baseAmount,
        experienceIncrease,
        educationDifferential,
        longevityBonus,
        longevityYearBonus;

    // Nurses Salary
    if (props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'occupational-therapist' || props.jobTitle === 'audiologist'){
        if(props.jobTitle === 'staff-nurse'){
            jobBaseAmountData = nurseBaseAmount;
            jobPayIncreaseData = nursePayIncrease;
            jobEductionData = nurseEducation;
            jobLongevityData = nurseLongevity;
        }
        if(props.jobTitle === 'head-nurse'){
            jobBaseAmountData = headNurseBaseAmount;
            jobPayIncreaseData = headNursePayIncrease;
            jobEductionData = headNurseEducation;
            jobLongevityData = headNurseLongevity;
        }
        if(props.jobTitle ==='supervisor-nurse'){
            jobBaseAmountData = supervisorNurseBaseAmount;
            jobPayIncreaseData = supervisorNursePayIncrease;
            jobEductionData = supervisorNurseEducation;
            jobLongevityData = supervisorNurseLongevity;
        }
        if(props.jobTitle === 'audiologist'){
            jobBaseAmountData = audiologistBaseAmount;
            jobPayIncreaseData = audiologistPayIncrease;
            jobEductionData = audiologistEducation;
            jobLongevityData = audiologistLongevity;
        }
        if(props.jobTitle === 'physical-therapist' || props.jobTitle === 'occupational-therapist'){
            jobBaseAmountData = therapistBaseAmount;
            jobPayIncreaseData = therapistPayIncrease;
            jobEductionData = therapistEducation;
            jobLongevityData = therapistLongevity;
            const totalPastExp = props.longevity + parseInt(props.termLength || 0);

            if(septFirst && 6 > totalPastExp && totalPastExp > 2){
                let inter = 5;
                if(parseInt(props.termLength || 0) >= 2){
                    inter = 3;
                }
                if(inter > props.longevity){
                    baseAmount = jobBaseAmountData[0]?.seniorYearOneTwo || 0;
                }
                else{
                    baseAmount = jobBaseAmountData[0]?.seniorYearThreeFour || 0;
                }
            }
            else if(3 > totalPastExp){
                baseAmount = jobBaseAmountData[0]?.base || 0;
            }
            else{
                baseAmount = jobBaseAmountData[0]?.senior || 0;
            }
        }
        else{
            baseAmount = jobBaseAmountData[0]?.base || 0;   
        }
        experienceIncrease = jobPayIncreaseData.find(item => item.id === (props.singleYearDifferential === "" ? 0 : String(props.singleYearDifferential).split('+')[0].trim()))?.expDiff || 0;
        educationDifferential = jobEductionData.find(item => item.id === formattedDegreeType)?.amount || 0;
        longevityBonus = jobLongevityData.find(item => item.id === longevityText)?.amount || 0;
        longevityYearBonus = jobLongevityData.find(item => item.id === longevityYearsText)?.amount || 0;
        totalPay = baseAmount + experienceIncrease + educationDifferential + longevityBonus;
    }

  return (
    <>
      <div className='content-box salary-schedule'>
        {props.adultEd == 'yes' && props.jobTitle == "paraprofessionals" ? <p className='content'>The NYC DOE salary schedule for paraprofessionals working in an adult education program is not available online. If you would like to confirm that your salary is correct, please call a UFT Salary Specialist at <a href="tel:212-331-6311" class='text-link white-text'>212-331-6311</a>.</p> : 
        <>
            <h3 className='title'>{salaryScheduleTitle}</h3>
            {error == true && <div className="alert-wrapper"><div className='alert warning'><p className='content'>Possible Error</p></div></div>}
            <div className={`flex ${props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse' ? 'flex-column' : 'flex-row'} space-between`}>
                {props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'occupational-therapist' || props.jobTitle === 'audiologist' ? 
                <>
                    <div>
                        <div className="pay-code-stub full-width total-pay-stub">
                            <p className='pay-code'>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(totalPay)}</p>
                            <table className='salary-schedule'>
                                <tr>
                                    <td>Base Salary:</td>
                                    <td>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(baseAmount)}</td>
                                </tr>
                                <tr>
                                    <td>{formattedDegreeType === 'Base Differential' ? 'Education Differential' : formattedDegreeType}:</td>
                                    <td>{educationDifferential === 0 ? 'None' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(educationDifferential)}</td>
                                </tr>
                                <tr>
                                    <td>Experience Differential{props.singleYearDifferential === "" || props.singleYearDifferential === 0 ? ":" : ` ${String(props.singleYearDifferential).split('+')[0].trim()}:`}</td>
                                    <td>{experienceIncrease === 0 ? 'None' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(experienceIncrease)}</td>
                                </tr>
                                <tr>
                                    <td>Longevity Differential{longevityText ? ` (${longevityText}):` : ':'}</td>
                                    <td>{longevityBonus === 0 ? 'None' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(longevityBonus)}</td>
                                </tr>
                                {((props.jobTitle === 'physical-therapist' || props.jobTitle === 'occupational-therapist' || props.jobTitle === 'audiologist') && 5 <= props.longevity) &&
                                    <tr>
                                        <td>Longevity Differential{longevityYearsText ? ` (${longevityYearsText}):` : ':'}</td>
                                        <td>{longevityYearBonus === 0 ? 'None' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(longevityYearBonus)}</td>
                                    </tr>
                                }
                            </table>
                        </div>
                    </div>
                </> : 
                <>
                    <div>
                    <p className='content'>ROW</p>
                        {props.jobTitle != 'paraprofessionals' ? <div className="pay-code-stub">
                            <p className='pay-code'>{stepNumber}{updatedStepLetter}{longevityCode}</p>
                        </div> : <div className="pay-code-stub full-width">
                            <p className='pay-code'>{convertedDifferential}</p>
                        </div>}
                    </div>
                    {(props.jobTitle != 'lab-tech' && props.jobTitle != 'paraprofessionals') &&  <div>
                        <p className='content'>COLUMN</p>
                        <div className="pay-code-stub">
                            <p className='pay-code' dangerouslySetInnerHTML={{__html: convertedDifferential}}></p>
                        </div>
                    </div>}
                </>}
            </div>
            {listContent}
        </>}
      </div>
    </>
  );
}

export default SalaryScheduleCodes;